import { ArrowLeft, Plus, Pill, X, Edit, Trash2, Check } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { message } from "antd";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import welcomeBg from "../../assets/homebg.png";
import medicationService from "../../services/MedicationService";
import homeService from "../../services/HomeService";

// Helper function to convert 24-hour time to 12-hour AM/PM format
const formatTimeTo12Hour = (time24: string): string => {
  if (!time24) return "";

  const [hours, minutes] = time24.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const hours12 = hours % 12 || 12;

  return `${String(hours12).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${period}`;
};


const MedicationManagement = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [medications, setMedications] = useState<any[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [takenMedications, setTakenMedications] = useState<Set<string>>(
    new Set()
  );
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    message: "",
  });
  const [formData, setFormData] = useState({
    name: "",
    frequency: "",
    time: "",
  });

  // Load medications from API on mount
  useEffect(() => {
    loadMedications();
  }, []);

  // Reload medications when page comes back into focus
  useEffect(() => {
    const handleFocus = () => {
      loadMedications();
    };

    const handleCustomEvent = () => {
      loadMedications();
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("medicationsUpdated", handleCustomEvent);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("medicationsUpdated", handleCustomEvent);
    };
  }, []);

  const loadMedications = async () => {
    try {
      setLoading(true);

      // Try Method 1: Direct medication API (GET /medication/crud/)
      const medications = await medicationService.getAllMedications();

      if (medications && medications.length > 0) {
        const processedMeds = medications.map(med => ({
          id: med.timing_id,
          timing_id: med.timing_id,
          medication_id: med.medication_id,
          name: med.medication_name,
          frequency: med.frequency,
          time: med.time,
          status: med.status,
        }));

        // Set taken medications based on API status
        const takenIds = new Set<string>();
        processedMeds.forEach(med => {
          if (med.status && med.status !== "not_taken") {
            takenIds.add(med.timing_id);
          }
        });
        setTakenMedications(takenIds);

        setMedications(processedMeds);
        return;
      }

      // Method 2: Home API (GET /food-home-page/)
      const homeData = await homeService.getHomePageData();
      const apiMedications = homeData?.["Medication Reminders"] || [];

      if (apiMedications.length > 0) {
        const convertedApiMeds = apiMedications.map((med: any) => ({
          id: med["Timing ID"],
          timing_id: med["Timing ID"],
          medication_id: med["Medication ID"],
          name: med["Medication Name"],
          frequency: med.Frequency,
          time: med["Medication Time"],
          status: med.Status,
        }));

        // Set taken medications based on API status from home API
        const takenIds = new Set<string>();
        convertedApiMeds.forEach(med => {
          if (
            med.status === "taken" ||
            med.status === "done" ||
            med.status === "completed"
          ) {
            takenIds.add(med.timing_id);
          }
        });
        setTakenMedications(takenIds);

        setMedications(convertedApiMeds);
        return;
      }

      setMedications([]);
    } catch (error) {
      console.error("❌ Error loading medications:", error);
      setMedications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMedication = async () => {
    if (formData.name && formData.frequency && formData.time) {
      try {
        setLoading(true);

        if (editingId) {
          // Update existing medication via API
          try {
            await medicationService.updateMedication(editingId, {
              medication_name: formData.name,
              time: formData.time,
              frequency: formData.frequency,
            });

            await loadMedications();
          } catch (error: any) {
            const errorMsg = error?.message || "";
            let userMessage = "";

            if (
              errorMsg.includes("Cannot read properties") ||
              errorMsg.includes("undefined")
            ) {
              userMessage =
                "Unable to update medication. Please try again or contact support.";
            } else if (errorMsg.includes("not available")) {
              userMessage =
                "Update feature is currently unavailable. Please try again later.";
            } else {
              userMessage =
                errorMsg || "Failed to update medication. Please try again.";
            }

            setErrorModal({
              isOpen: true,
              message: userMessage,
            });
          }
          setEditingId(null);
        } else {
          // Add new medication via API
          await medicationService.addMedication({
            medication_name: formData.name,
            time: formData.time,
            frequency: formData.frequency,
          });


          // Reload medications from API to get updated list
          await loadMedications();
        }

        setFormData({ name: "", frequency: "", time: "" });
        setIsModalOpen(false);

        // Dispatch custom event to notify other components (like HomePage)
        window.dispatchEvent(new CustomEvent("medicationsUpdated"));
      } catch (error: any) {
        const errorMsg = error?.message || "";
        let userMessage = "";

        if (
          errorMsg.includes("Cannot read properties") ||
          errorMsg.includes("undefined")
        ) {
          userMessage =
            "Unable to add medication. Please check all fields and try again.";
        } else if (
          errorMsg.includes("Network") ||
          errorMsg.includes("timeout")
        ) {
          userMessage =
            "Network error. Please check your internet connection and try again.";
        } else if (errorMsg.includes("500") || errorMsg.includes("server")) {
          userMessage = "Server error occurred. Please try again later.";
        } else if (errorMsg.includes("already exists")) {
          userMessage =
            "This medication already exists. Please use a different name.";
        } else {
          userMessage =
            errorMsg || "Failed to add medication. Please try again.";
        }

        setErrorModal({
          isOpen: true,
          message: userMessage,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (field: string, value: string) => {
    // Validate frequency field - only allow letters and spaces
    if (field === "frequency") {
      // Remove numbers and special characters, keep only letters and spaces
      const sanitizedValue = value.replace(/[^a-zA-Z\s]/g, "");
      setFormData(prev => ({ ...prev, [field]: sanitizedValue }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleDeleteMedication = async (id: string) => {
    try {
      setLoading(true);

      // Call API to delete medication
      await medicationService.deleteMedication(id);

      // Reload medications from API to get updated list
      await loadMedications();

      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent("medicationsUpdated"));
    } catch (error: any) {
      console.error("❌ Error deleting medication:", error);

      const errorMsg = error?.message || "";
      let userMessage = "";

      if (errorMsg.includes("not available")) {
        userMessage = "Delete feature is currently unavailable. Please try again later.";
      } else if (errorMsg.includes("Network") || errorMsg.includes("timeout")) {
        userMessage = "Network error. Please check your internet connection and try again.";
      } else if (errorMsg.includes("500") || errorMsg.includes("server")) {
        userMessage = "Server error occurred. Please try again later.";
      } else {
        userMessage = errorMsg || "Failed to delete medication. Please try again.";
      }

      setErrorModal({
        isOpen: true,
        message: userMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditMedication = (medication: any) => {
    setFormData({
      name: medication.name,
      frequency: medication.frequency ?? "",
      time: medication.time ?? "",
    });
    setEditingId(medication.id);
    setIsModalOpen(true);
  };

  const handleMedicationDone = async (
    medicationName: string,
    timingId: string
  ) => {
    try {
      // Call API to mark as done
      await medicationService.markMedicationDone(timingId);

      // Update UI state immediately
      setTakenMedications(prev => new Set(prev).add(timingId));
      // Reload medications to get updated status from server
      setTimeout(() => {
        loadMedications();
      }, 500);

      // Notify other components
      window.dispatchEvent(new CustomEvent("medicationsUpdated"));
    } catch (error: any) {
      // Convert technical errors to user-friendly messages
      let userMessage = "";
      const errorMsg = error?.message || "";

      if (errorMsg.includes("not found for today")) {
        userMessage =
          "This medication timing is no longer available for today. Please check your medication list.";
      } else if (
        errorMsg.includes("Cannot read properties") ||
        errorMsg.includes("undefined")
      ) {
        userMessage =
          "Unable to process your request. Please try again or contact support.";
      } else if (errorMsg.includes("Network") || errorMsg.includes("timeout")) {
        userMessage =
          "Network error. Please check your internet connection and try again.";
      } else if (errorMsg.includes("500") || errorMsg.includes("server")) {
        userMessage = "Server error occurred. Please try again later.";
      } else if (errorMsg) {
        userMessage = errorMsg;
      } else {
        userMessage = "Failed to mark medication as taken. Please try again.";
      }

      // Show custom error popup
      setErrorModal({
        isOpen: true,
        message: userMessage,
      });

      // Reload medications to sync with server
      loadMedications();
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({ name: "", frequency: "", time: "" });
    setEditingId(null);
  };

  return (
    <div
      className="relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 overflow-y-auto pb-16"
        style={{ height: "100vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/glucera" + GluceraNavigationRoutes.HOME)}
            className="rounded-xl p-2"
          >
            <ArrowLeft className="h-6 w-6 text-muted-foreground" />
          </Button>
          <div className="flex-1 text-center">
            <h1 className="text-title3 font-semibold text-foreground">
              Medication Reminders
            </h1>
            <p className="text-subhead text-muted-foreground">
              Track your medications
            </p>
          </div>
          <Button
            size="sm"
            className="rounded-xl bg-gradient-primary px-4 py-2 text-primary-foreground shadow-primary hover:opacity-90"
            onClick={() => setIsModalOpen(true)}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add
          </Button>
        </div>

        {/* Main Content */}
        <div className="px-4">
          {loading && medications.length === 0 ? (
            /* Loading State */
            <div className="mt-20 text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent" />
              <p className="mt-4 text-subhead text-muted-foreground">
                Loading medications...
              </p>
            </div>
          ) : medications.length === 0 ? (
            /* Empty State Card */
            <div className="mt-20">
              <div className="rounded-xl border-2 border-border bg-card p-8 text-center shadow-sm">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-100 shadow-sm dark:bg-orange-950">
                  <Pill className="h-8 w-8 text-orange-600 dark:text-orange-400" />
                </div>
                <h3 className="mb-2 text-callout font-semibold text-foreground">
                  No medications added yet
                </h3>
                <p className="text-subhead text-muted-foreground">
                  Click "Add" to create your first medication reminder
                </p>
              </div>
            </div>
          ) : (
            /* Medication List - Show all medications */
            (() => {
              return (
                /* Medication List */
                <div className="space-y-3">
                  {medications.map((med, index) => (
                    <Card
                      key={med.id ?? index}
                      className="rounded-xl border-2 border-border bg-card p-4 shadow-sm transition-shadow hover:shadow-md"
                    >
                      <div className="flex items-center gap-2">
                        {/* Left: Icon */}
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-orange-100 dark:bg-orange-950">
                          <Pill className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                        </div>

                        {/* Middle: Details */}
                        <div className="min-w-0 flex-1">
                          <h3 className="truncate text-subhead font-semibold text-foreground">
                            {med.name}
                          </h3>
                          <div className="flex items-center gap-1 text-caption1 text-muted-foreground">
                            <span className="truncate">{med.frequency}</span>
                            <span className="flex-shrink-0">•</span>
                            <span className="flex-shrink-0 font-medium">
                              {formatTimeTo12Hour(med.time)}
                            </span>
                          </div>
                        </div>

                        {/* Right: Action Buttons - Compact */}
                        <div className="flex flex-shrink-0 items-center gap-0.5">
                          <Button
                            variant={
                              takenMedications.has(med.timing_id ?? med.id)
                                ? "secondary"
                                : "default"
                            }
                            size="sm"
                            onClick={() =>
                              handleMedicationDone(
                                med.name,
                                med.timing_id ?? med.id
                              )
                            }
                            disabled={takenMedications.has(
                              med.timing_id ?? med.id
                            )}
                            className={`h-6 px-1.5 text-compact-xs font-medium ${takenMedications.has(med.timing_id ?? med.id)
                              ? "bg-green-100 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-400"
                              : "bg-gradient-primary text-primary-foreground shadow-primary hover:opacity-90"
                              }`}
                          >
                            {takenMedications.has(med.timing_id ?? med.id) ? (
                              <>
                                <Check className="mr-0.5 h-2.5 w-2.5" /> Taken
                              </>
                            ) : (
                              "Done"
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditMedication(med)}
                            className="h-6 w-6 text-muted-foreground hover:bg-muted hover:text-foreground"
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteMedication(med.id)}
                            className="h-6 w-6 text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              );
            })()
          )}
        </div>
      </div>

      {/* Add Medication Modal - Center Popup */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            className="w-full max-w-md rounded-2xl bg-card p-6 shadow-xl"
            style={{ maxHeight: "85vh", overflowY: "auto" }}
          >
            {/* Modal Header */}
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-title2 font-semibold text-foreground">
                {editingId ? "Edit Medication" : "Add Medication"}
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCloseModal}
                className="h-10 w-10 rounded-full"
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </Button>
            </div>

            {/* Form Fields */}
            <div className="space-y-4">
              {/* Medication Name */}
              <div>
                <Label className="mb-2 block text-subhead font-medium text-foreground">
                  Medication Name
                </Label>
                <Input
                  placeholder="e.g., Vitamin D"
                  value={formData.name}
                  onChange={e => handleInputChange("name", e.target.value)}
                  disabled={!!editingId}
                  className="rounded-xl border-2 border-border px-4 py-4 text-callout focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                />
              </div>

              {/* Frequency */}
              <div>
                <Label className="mb-2 block text-subhead font-medium text-foreground">
                  Frequency
                </Label>
                <Input
                  placeholder="e.g., With breakfast"
                  value={formData.frequency}
                  onChange={e => handleInputChange("frequency", e.target.value)}
                  className="rounded-xl border-2 border-border px-4 py-4 text-callout focus:border-blue-500"
                />
              </div>

              {/* Time */}
              <div>
                <Label className="mb-2 block text-subhead font-medium text-foreground">
                  Time
                </Label>
                <input
                  type="time"
                  value={formData.time}
                  onChange={e => handleInputChange("time", e.target.value)}
                  className="w-full rounded-xl border-2 border-border bg-background px-4 py-3 text-callout text-foreground focus:border-blue-500 focus:outline-none"
                />
              </div>
            </div>

            {/* Modal Actions - Mobile Optimized */}
            <div className="mt-8 flex gap-3">
              <Button
                variant="outline"
                onClick={handleCloseModal}
                className="flex-1 rounded-xl border-2 border-border py-4 text-callout font-medium"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddMedication}
                className="flex-1 rounded-xl bg-gradient-primary py-4 text-callout font-medium text-primary-foreground shadow-primary hover:opacity-90"
                disabled={
                  !formData.name ||
                  !formData.frequency ||
                  !formData.time ||
                  loading
                }
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent" />
                    <span>Saving...</span>
                  </div>
                ) : editingId ? (
                  "Update"
                ) : (
                  "Add Medication"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Error Popup Modal */}
      {errorModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-card p-6 shadow-xl">
            {/* Error Icon */}
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <X className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>

            {/* Error Message */}
            <h3 className="mb-2 text-center text-title3 font-semibold text-foreground">
              Error
            </h3>
            <p className="mb-6 text-center text-subhead text-muted-foreground">
              {errorModal.message}
            </p>

            {/* Close Button */}
            <Button
              onClick={() => setErrorModal({ isOpen: false, message: "" })}
              className="w-full rounded-xl bg-red-600 py-3 text-callout font-medium hover:bg-red-700"
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicationManagement;
